import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Lock, Shield, CreditCard, Check, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SubscribePage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/api/payments/status/${sid}`);
      
      if (response.data.payment_status === 'paid') {
        await refreshUser();
        toast.success('Payment successful! Welcome to Premium!');
        setCheckingPayment(false);
        navigate('/anonymous');
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  };

  const handleSubscribe = async (method) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/payments/create-checkout`, {
        origin_url: window.location.origin,
        payment_method: method
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error(response.data.message || 'Payment method not available');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-[#F7F5F2]">
        <Header />
        <div className="content-container pt-20">
          <div className="max-w-md mx-auto py-16 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-[#6B8E7B] mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-stone-800 mb-2 font-['Outfit']">Verifying Payment...</h2>
            <p className="text-stone-600">Please wait while we confirm your payment.</p>
          </div>
        </div>
      </div>
    );
  }

  if (user?.is_premium) {
    return (
      <div className="min-h-screen bg-[#F7F5F2]">
        <Header />
        <div className="content-container pt-20">
          <div className="max-w-md mx-auto py-16 text-center">
            <div className="w-20 h-20 rounded-full bg-[#6B8E7B]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-[#6B8E7B]" />
            </div>
            <h2 className="text-2xl font-bold text-stone-800 mb-2 font-['Outfit']">You're Premium!</h2>
            <p className="text-stone-600 mb-6">You have full access to all premium features.</p>
            <Button
              onClick={() => navigate('/anonymous')}
              className="btn-premium"
              data-testid="go-to-anonymous-button"
            >
              <Lock className="h-4 w-4 mr-2" />
              Go to Anonymous Sharing
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      <Header />
      
      <div className="content-container pt-20">
        <div className="max-w-2xl mx-auto py-12">
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-[#E27D60]/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-[#E27D60]" />
            </div>
            <h1 className="text-3xl font-bold text-stone-800 mb-3 font-['Outfit']">Go Premium</h1>
            <p className="text-lg text-stone-600 max-w-md mx-auto">
              Unlock anonymous sharing and join a safe space where you can express yourself freely.
            </p>
          </div>

          {/* Pricing Card */}
          <Card className="anonymous-card rounded-2xl max-w-md mx-auto mb-8">
            <CardHeader className="text-center pb-2">
              <span className="premium-badge mx-auto mb-2">Premium</span>
              <CardTitle className="text-4xl font-bold text-stone-800 font-['Outfit']">
                €5<span className="text-lg font-normal text-stone-500">/month</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#6B8E7B]/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-4 w-4 text-[#6B8E7B]" />
                  </div>
                  <span className="text-stone-700">Share worries anonymously</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#6B8E7B]/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-4 w-4 text-[#6B8E7B]" />
                  </div>
                  <span className="text-stone-700">Read and support others</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#6B8E7B]/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-4 w-4 text-[#6B8E7B]" />
                  </div>
                  <span className="text-stone-700">Your identity stays protected</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#6B8E7B]/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-4 w-4 text-[#6B8E7B]" />
                  </div>
                  <span className="text-stone-700">Support nursing community</span>
                </li>
              </ul>

              <div className="space-y-3">
                <Button
                  onClick={() => handleSubscribe('stripe')}
                  className="w-full btn-premium h-12 text-base"
                  disabled={loading}
                  data-testid="subscribe-stripe-button"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="h-5 w-5 mr-2" />
                  )}
                  Pay with Card (Stripe)
                </Button>
                
                <p className="text-xs text-center text-stone-500">
                  Secure payment powered by Stripe
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-6 text-stone-400 text-sm">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              <span>Privacy Protected</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscribePage;
